import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ADJUVANT_COLLECTION,
  BIOSTIMULANT_COLLECTION,
  CHEMICAL_COLLECTION,
  OPERATION_COLLECTION,
  SEEDSPECIES_COLLECTION,
  SOILINPUT_COLLECTION,
} from 'constants/gcConstants';
import { EMPTY_GUID } from 'constants/appConstants';
import { UPDATE_DEFAULTS_NOT_SAVED_ERROR } from 'constants/messageConstants';
import {
  M_ADJUVANT_QUERY_KEY,
  M_BIOSTIMULANT_QUERY_KEY,
  M_CHEMICAL_QUERY_KEY,
  M_CROPS_QUERY_KEY,
  M_OPERATION_QUERY_KEY,
  M_SEARCH_ADJUVANT_QUERY_KEY,
  M_SEARCH_BIOSTIMULANT_QUERY_KEY,
  M_SEARCH_CHEMICAL_QUERY_KEY,
  M_SEARCH_CROPS_QUERY_KEY,
  M_SEARCH_OPERATION_QUERY_KEY,
  M_SEARCH_SOILINPUT_QUERY_KEY,
  M_SOILINPUT_QUERY_KEY,
} from 'constants/queryKeys';
import { fetchMCollection } from 'hooks/useFetchMCollection';
import {
  clearMOrgAccessCache,
  isForbidden,
  useResolveMOrgId,
} from 'hooks/useResolveMOrgId';
import type { IUnit } from 'interfaces/IInput';
import type { IInputStep } from 'interfaces/IInputStep';
import { InputStepType } from 'interfaces/IInputStep';
import type { MBase } from 'interfaces/IMasterDb';
import {
  MasterDbService,
  MWritableCollectionKey,
} from 'services/MasterDbServices';
import { v4 as uuidv4 } from 'uuid';
import {
  buildMBase,
  buildMUpdateBase,
  buildMWriteBody,
  MProductDefaults,
  pickChangedDefaults,
  readDeviceId,
  sortByDateModifiedDesc,
} from 'utils/mDocumentFactory';

export type UpdateDefaultsOutcome = 'saved' | 'nothingToUpdate';

interface UpdateDefaultsConfig {
  collection: MWritableCollectionKey;
  mQueryKey: string;
  searchQueryKey: string;
  // The row's values as the product's defaults: price for most products, cost
  // for crops, and cost + unit for operations.
  toDefaults: (row: RateCostRow) => MProductDefaults;
}

interface RateCostRow {
  rate: number | null;
  appUnit: IUnit | null;
  cost: number | null;
}

const productDefaults = (row: RateCostRow): MProductDefaults => ({
  rate: row.rate,
  rateUnit: row.appUnit,
  price: row.cost,
});

// Seed treatment is left out until its catalogue is real (OP-2401).
const UPDATE_DEFAULTS_CONFIG: Partial<
  Record<InputStepType, UpdateDefaultsConfig>
> = {
  [InputStepType.CropProtection]: {
    collection: CHEMICAL_COLLECTION,
    mQueryKey: M_CHEMICAL_QUERY_KEY,
    searchQueryKey: M_SEARCH_CHEMICAL_QUERY_KEY,
    toDefaults: productDefaults,
  },
  [InputStepType.Adjuvant]: {
    collection: ADJUVANT_COLLECTION,
    mQueryKey: M_ADJUVANT_QUERY_KEY,
    searchQueryKey: M_SEARCH_ADJUVANT_QUERY_KEY,
    toDefaults: productDefaults,
  },
  [InputStepType.SoilInput]: {
    collection: SOILINPUT_COLLECTION,
    mQueryKey: M_SOILINPUT_QUERY_KEY,
    searchQueryKey: M_SEARCH_SOILINPUT_QUERY_KEY,
    toDefaults: productDefaults,
  },
  [InputStepType.Biostimulant]: {
    collection: BIOSTIMULANT_COLLECTION,
    mQueryKey: M_BIOSTIMULANT_QUERY_KEY,
    searchQueryKey: M_SEARCH_BIOSTIMULANT_QUERY_KEY,
    toDefaults: productDefaults,
  },
  [InputStepType.Seed]: {
    collection: SEEDSPECIES_COLLECTION,
    mQueryKey: M_CROPS_QUERY_KEY,
    searchQueryKey: M_SEARCH_CROPS_QUERY_KEY,
    toDefaults: (row) => ({
      rate: row.rate,
      rateUnit: row.appUnit,
      cost: row.cost,
    }),
  },
  [InputStepType.Application]: {
    collection: OPERATION_COLLECTION,
    mQueryKey: M_OPERATION_QUERY_KEY,
    searchQueryKey: M_SEARCH_OPERATION_QUERY_KEY,
    toDefaults: (row) => ({ cost: row.cost, operationUnit: row.appUnit }),
  },
};

/** Input rows whose rate, cost and unit can be saved as the product's defaults. */
export const isUpdateDefaultsSupported = (step: IInputStep): boolean =>
  !!UPDATE_DEFAULTS_CONFIG[step.stepType] &&
  !!step.globalId &&
  step.globalId !== EMPTY_GUID &&
  'cost' in step;

type MProductDoc = MBase & MProductDefaults;

// One request at a time per product, so a double click or a second row for the
// same product can't each decide "Create" and make two M documents.
const queueByGlobalId = new Map<string, Promise<unknown>>();

const runQueued = <T>(globalId: string, task: () => Promise<T>): Promise<T> => {
  const previous = queueByGlobalId.get(globalId) ?? Promise.resolve();
  const run = previous.catch(() => undefined).then(task);
  const tail = run.catch(() => undefined);
  queueByGlobalId.set(globalId, tail);
  void tail.then(() => {
    if (queueByGlobalId.get(globalId) === tail) {
      queueByGlobalId.delete(globalId);
    }
  });
  return run;
};

/**
 * Saves an input row's rate, cost and unit as the product's defaults
 * (OP-2499): updates the user's M document for the product, or creates one
 * (an override of the G product) when there isn't one. Only values that differ
 * are sent, and never empty ones, because null on an M document hides the G
 * default. Rows already in plans are untouched.
 */
export const useUpdateProductDefaults = () => {
  const queryClient = useQueryClient();
  const resolveMOrgId = useResolveMOrgId();

  return useMutation<UpdateDefaultsOutcome, Error, IInputStep>({
    mutationFn: async (step) => {
      const config = UPDATE_DEFAULTS_CONFIG[step.stepType];
      const globalId = step.globalId;
      if (!config || !globalId || !('cost' in step)) {
        throw new Error(UPDATE_DEFAULTS_NOT_SAVED_ERROR);
      }
      const row: RateCostRow = {
        rate: 'rate' in step ? (step.rate ?? null) : null,
        appUnit: 'appUnit' in step ? (step.appUnit ?? null) : null,
        cost: step.cost ?? null,
      };

      return runQueued(globalId, async () => {
        const performUpdate = async (
          retried: boolean
        ): Promise<UpdateDefaultsOutcome> => {
          const mOrgId = await resolveMOrgId();
          try {
            const docs = await fetchMCollection<MProductDoc>(
              config.collection,
              mOrgId
            );
            const latest = sortByDateModifiedDesc(
              docs.filter(
                (doc) => doc.globalId === globalId && doc.isActive !== false
              )
            )[0];

            // With no M document the G default isn't in hand, so everything the
            // row holds is saved; the result is the same default either way.
            const changed = pickChangedDefaults(
              latest ?? {},
              config.toDefaults(row)
            );
            if (Object.keys(changed).length === 0) return 'nothingToUpdate';

            const deviceId = readDeviceId();
            if (latest) {
              const response = await MasterDbService.UpdateMDocument(
                config.collection,
                buildMWriteBody(buildMUpdateBase(latest, deviceId), changed)
              );
              // 200 with no changes means the server skipped it (older than the
              // stored document), so the default was not saved.
              if (!response.changes?.length) {
                throw new Error(UPDATE_DEFAULTS_NOT_SAVED_ERROR);
              }
            } else {
              await MasterDbService.CreateMDocument(
                config.collection,
                buildMWriteBody(
                  buildMBase({
                    id: uuidv4(),
                    orgId: mOrgId,
                    globalId,
                    deviceId,
                  }),
                  changed
                )
              );
            }

            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [config.mQueryKey] }),
              queryClient.invalidateQueries({
                queryKey: [config.searchQueryKey],
              }),
            ]);
            return 'saved';
          } catch (err) {
            if (isForbidden(err) && !retried) {
              clearMOrgAccessCache(queryClient);
              return performUpdate(true);
            }
            throw err;
          }
        };
        return performUpdate(false);
      });
    },
  });
};
